import React, { useState } from 'react'
import Skeleton, { SkeletonTheme } from 'react-loading-skeleton'
import { useTranslation } from 'react-i18next'
import { ReactComponent as NotFoundIcon } from '../../assets/svg/NotFoundIcon.svg'
import '../../styles/MediaWithFallback.css'

/**
 * Renders the "media not available" placeholder shown when an image or video
 * fails to load. Mirrors the Alkalma mobile app behaviour.
 */
export const MediaUnavailable = ({ type = 'image', className = '' }) => {
	const { t } = useTranslation()
	return (
		<div className={`media-unavailable ${className}`}>
			<NotFoundIcon />
			<span>{t(type === 'video' ? 'videoNotAvailable' : 'imageNotAvailable')}</span>
		</div>
	)
}

const buildImageSrc = (url, width) => {
	if (!url) return url
	if (!width) return url
	return url.includes('width') ? url : `${url}&width=${width}`
}

/**
 * Image / video renderer with graceful fallback.
 *
 * - While an image loads a skeleton is shown.
 * - If the image or video fails to load, a "not available" placeholder is
 *   rendered instead of a broken element.
 * - `onClick` (used to open the full-screen image modal) is only wired while the
 *   media is healthy, so a failed tile can never open an empty modal.
 */
const MediaWithFallback = ({
	url,
	type = 'image',
	width,
	containerClassName = '',
	containerStyle,
	imgClassName = '',
	imgStyle,
	onClick,
	alt = '',
}) => {
	const { t } = useTranslation()
	const [loading, setLoading] = useState(type === 'image')
	const [error, setError] = useState(false)

	if (error) {
		return (
			<div className={containerClassName} style={containerStyle}>
				<MediaUnavailable type={type} />
			</div>
		)
	}

	if (type === 'video') {
		return (
			<div className={containerClassName} style={containerStyle}>
				<video controls controlsList="nodownload" className={imgClassName} src={url} onError={() => setError(true)}>
					{t('yourBrowserDoesNotSupportHTMLVideo')}
				</video>
			</div>
		)
	}

	const clickable = typeof onClick === 'function'
	return (
		<div
			className={containerClassName}
			onClick={clickable ? onClick : undefined}
			style={{ position: 'relative', ...(clickable ? { cursor: 'pointer' } : null), ...containerStyle }}
		>
			{loading && (
				<div className="media-loading" aria-hidden="true">
					<SkeletonTheme baseColor="var(--grey)" highlightColor="#efefef">
						<Skeleton height="100%" width="100%" style={{ display: 'block', lineHeight: 1 }} />
					</SkeletonTheme>
				</div>
			)}
			<img
				src={buildImageSrc(url, width)}
				alt={alt}
				className={imgClassName}
				style={loading ? { ...imgStyle, opacity: 0 } : imgStyle}
				onLoad={() => setLoading(false)}
				onError={() => {
					setLoading(false)
					setError(true)
				}}
			/>
		</div>
	)
}

export default MediaWithFallback
